import { Avatar, Button, Chip, ListItem, ListItemAvatar, ListItemButton, ListItemText } from "@mui/material";
import { DRAWER_MAX_SELECTION_COUNT } from "app/appConstants";
import { GenericAbortSignal } from "axios";
import { MprcSpinnerIndicator } from "components";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { ChannelSelectorResponse, ChannelSelectorResponsePaginatedList, UserSelectionEntityType, UserSelectionEntry } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChildComponentProps } from "react-window";
import { GenericListDrawer, GenericListFilter, GenericListItemsType } from "views/common/GenericListDrawer";

////////////////////////////////////////////////////////////////////////////////

type ItemData = GenericListItemsType<ChannelSelectorResponse>;

////////////////////////////////////////////////////////////////////////////////

export type ChannelSelectorDrawerResult = {
    channelId: string;
    name: string;
    thumbnailId?: string | null;
};

interface IPros {
    title?: string;
    subtitle?: string;
    multi?: boolean;
    onClose: (group: ChannelSelectorDrawerResult[] | undefined) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const ChannelSelectorDrawer: React.FC<IPros> = (props) => {

    const api = useCoreApi();
    const session = useSession();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("knowledge");

    const { title, subtitle, multi = false, onClose } = props;

    const [saveSelection, setSaveSelection] = useState<ChannelSelectorResponse[] | undefined>(undefined);
    const [selected, setSelection] = useState<ChannelSelectorResponse[]>([]);

    ////////////////////////////////////////////////////////////////////////////////

    const handleGetData = (genericFilter: GenericListFilter, signal: GenericAbortSignal | undefined): Promise<ChannelSelectorResponsePaginatedList> => {
        return new Promise((resolve, reject) => {
            api.v1.knowledgeSelectors.channels({
                ...genericFilter,
                ignoreSelectionHistory: genericFilter.ignoreSelectionHistory ?? false
            }, { signal })
                .then((response) => resolve(response.data))
                .catch(reject);
        });
    };

    /**
     * Renderiza uma linha da Lista.
     * @param props
     */
    function renderRow(props: ListChildComponentProps<ItemData>) {
        const { index, style } = props;
        const { items, onViewerClick } = props.data;

        const item = items[index];
        if (item == undefined) return <></>;

        const hasDivider = index > 0 && items[index].fromHistory === false && items[index - 1].fromHistory === true;

        return (
            <div key={index} style={style}>
                <ListItem disablePadding disableGutters>
                    <ListItemButton disableGutters onClick={() => onViewerClick && onViewerClick(item)}
                        sx={(theme) => ({
                            marginTop: hasDivider ? "4px" : undefined,
                            paddingTop: hasDivider ? "4px" : theme.spacing(1),
                            borderTop: hasDivider ? "solid 2px" : undefined,
                            borderTopColor: hasDivider ? theme.palette.divider : undefined
                        })}
                    >
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                            <Avatar
                                src={item.thumbnailUrl}
                                sx={(theme) => ({ width: 32, height: 32, backgroundColor: theme.palette.grey[200] })}
                            />
                        </ListItemAvatar >
                        <ListItemText
                            primary={item.name}
                            title={item.name}
                            slotProps={
                                {
                                    primary: { noWrap: true, variant: "h6" }
                                }
                            }
                        />
                    </ListItemButton>
                </ListItem>
            </div>
        );
    }

    // Ao selecionar um item da lista.
    const onItemClick = (item: ChannelSelectorResponse) => {
        if (!multi)
            setSaveSelection([item]);
        else {
            if (selected.find(p => p.channelId == item.channelId) == null) {
                if (selected.length == DRAWER_MAX_SELECTION_COUNT)
                    enqueueSnackbar(t("Selector.SelectionLimitedMessage", { count: DRAWER_MAX_SELECTION_COUNT, ns: "shared" }), { variant: "warning" });
                else
                    setSelection([...selected, item]);
            }
            else {
                enqueueSnackbar(t("Selector.AlreadySelectedMessage", { name: item.name, ns: "shared" }), { variant: "error" });
            }
        }
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    /// Salva itens selecionados e retorna.
    useEffect(() => {
        if (saveSelection != undefined) {
            var payload = {
                userId: session.user.userId,
                entries: saveSelection.filter(p => !p.fromHistory).map((v) => ({
                    entityType: UserSelectionEntityType.Channel,
                    entityId: v.channelId,
                    otherInfo: ""
                } as UserSelectionEntry))
            };

            if (payload.entries.length > 0) {
                api.v1.user.saveSelectionHistory(payload)
                    .then(() => {
                        onClose(saveSelection);
                    })
                    .catch((e) => {
                        setSaveSelection(undefined);
                        MprcRequestSnackbarErrorReport(e);
                    });
            }
            else {
                onClose(saveSelection);
            }
        }
    }, [saveSelection]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <GenericListDrawer
            title={title || t("Channel.ChannelSelectorDrawer.Title")}
            subtitle={subtitle}
            itemHeight={40}
            onClose={() => onClose(undefined)}
            onGetData={handleGetData}
            onViewerClick={onItemClick}
            emptyDataMessage={t("Channel.ChannelSelectorDrawer.FilterEmptyMessage")}
            emptyFilterMessage={t("Channel.ChannelSelectorDrawer.NoRecordsFoundText")}
            renderRow={renderRow}
            selection="history"
            footer={
                <div style={{ width: "100%" }}>
                    {
                        multi && selected.length > 0 &&
                        <>
                            <div style={{ maxHeight: 200, overflow: "auto" }}>
                                <p><small>{t("Selector.SelectedText", { ns: "shared" })}:</small></p>
                                {
                                    selected.map((item, index) => (
                                        <Chip
                                            key={index}
                                            label={item.name}
                                            size="small"
                                            sx={{ mb: 0.5, mr: 0.5 }}
                                            avatar={<Avatar src={item.thumbnailUrl} />}
                                            onDelete={() =>
                                                setSelection(selected.filter(p => p.channelId != item.channelId))
                                            }
                                        />
                                    ))
                                }
                            </div>
                            <hr />
                            <div style={{ textAlign: "center" }}>
                                <Button
                                    variant="contained"
                                    disabled={saveSelection != undefined}
                                    endIcon={saveSelection == undefined ? undefined : <MprcSpinnerIndicator />}
                                    onClick={() => setSaveSelection(selected)}
                                >
                                    {t("Selector.AddSelectedButtonText", { ns: "shared" })}
                                </Button >
                            </div>
                        </>
                    }
                </div>
            }
        />
    );
};
